import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DateRangePicker from "../../components/DateRangePicker";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../assets/global/url";
import { Link } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../assets/img/NoAccess.png";
import useDecodeToken from "../../hooks/customHook/useDecodeToken";
import CurrencySelector from "../../components/CurrencySelector";
import PendingStatusTab from "./components/expenses_components/pending_tab";
import { Tabs, Tab } from "react-bootstrap";
import ExportOptionsModal from "../../components/Export/ExportOptionsModal";
import PdfPreviewModal, {
  usePdfPreview,
} from "../../components/Export/PdfPreviewModal";
import expensesExportConfig from "./components/expenses_components/Export/expensesExportConfig";
import { getMonthBoundaries, initializeCutoff } from "../../utils/newdate";
import FilterStore from "../../hooks/customHook/FilterStore";
import { formatToTwoDecimal } from "../../utils/numberFormatter";

const Expenses = ({ authrztn }) => {
  const userLoggedID = useDecodeToken();

  const [currencyId, setCurrencyId] = useState(() =>
    FilterStore.getCurrencyId(),
  );
  const [widgetCurrencySymbol, setWidgetCurrencySymbol] = useState(() =>
    FilterStore.getCurrencyName(),
  );

  useEffect(() => {
    FilterStore.saveCurrencyId(currencyId);
  }, [currencyId]);

  useEffect(() => {
    FilterStore.saveCurrencyName(widgetCurrencySymbol);
  }, [widgetCurrencySymbol]);

  const validtabs = ["pending", "approved"];
  const [activekeyTab, setActivekeyTab] = useState(() => {
    const saved = FilterStore.getTab();
    return validtabs.includes(saved) ? saved : "pending";
  });

  const [isLoading, setIsLoading] = useState(false);
  const [lastCutoffExpenses, setLastCutoffExpenses] = useState(0);
  const [currentTotalExpense, setCurrentTotalExpense] = useState(0);
  const [totalIssued, setTotalIssued] = useState(0);
  const [currentCutoffExpense, setCurrentCutoffExpense] = useState(0);

  const [selectedCutOff, setSelectedCutOff] = useState({
    id: "All",
    from: null,
    to: null,
  });

  const [pendingCount, setPendingCount] = useState(0);
  const [exportFormat, setExportFormat] = useState(null);
  const pdfPreview = usePdfPreview();

  const fetchAllPendingCount = useCallback(async () => {
    if (!userLoggedID) return;
    try {
      const res = await axios.get(
        `${BASE_URL}/expenses/getExpensesData-search`,
        {
          params: {
            status_tab: "For-Approval",
            searchText: "",
            filterColumn: "all",
            currencyId: "All",
            userLoggedID,
            limit: 1,
            page: 1,
          },
        },
      );
      setPendingCount(res.data.totalItems ?? 0);
    } catch (error) {
      console.error("Failed to fetch pending count:", error);
    }
  }, [userLoggedID]);

  useEffect(() => {
    fetchAllPendingCount();
  }, [fetchAllPendingCount]);

  const fetchWidgetData = useCallback(
    async (cutoff) => {
      if (!userLoggedID) return;

      let resolvedFrom = cutoff?.from;
      let resolvedTo = cutoff?.to;

      // No date range (Clear Filter mode) — resolve real min/max expenses_date
      // so widgets show all-time totals instead of zeros.
      if (!resolvedFrom || !resolvedTo) {
        try {
          const rangeRes = await axios.get(
            `${BASE_URL}/expenses/getExpensesDateRange`,
          );
          const { minDate, maxDate } = rangeRes.data;

          if (!minDate || !maxDate) {
            setLastCutoffExpenses(0);
            setCurrentTotalExpense(0);
            setTotalIssued(0);
            setCurrentCutoffExpense(0);
            return;
          }

          resolvedFrom = minDate;
          // Use the last day of the month of the latest expenses_date as end boundary.
          const maxDateObj = new Date(maxDate);
          const lastDay = new Date(
            maxDateObj.getFullYear(),
            maxDateObj.getMonth() + 1,
            0,
          );
          resolvedTo = lastDay.toISOString().split("T")[0];
        } catch (err) {
          console.error("Error fetching expenses date range:", err);
          setLastCutoffExpenses(0);
          setCurrentTotalExpense(0);
          setTotalIssued(0);
          setCurrentCutoffExpense(0);
          return;
        }
      }

      const baseParams = {
        startDate: resolvedFrom,
        endDate: resolvedTo,
        currencyId,
        userLoggedID,
      };

      try {
        const [lastRes, currentTotalRes, totalIssuedRes, currentCutoffRes] =
          await Promise.allSettled([
            axios.get(`${BASE_URL}/expenses/fetchLastCutoffExpense`, {
              params: baseParams,
            }),
            axios.get(`${BASE_URL}/expenses/fetchCurrentTotalExpense`, {
              params: baseParams,
            }),
            axios.get(`${BASE_URL}/expenses/fetchTotalIssued`, {
              params: baseParams,
            }),
            axios.get(`${BASE_URL}/expenses/fetchCurrentCutoffExpense`, {
              params: baseParams,
            }),
          ]);

        setLastCutoffExpenses(
          lastRes.status === "fulfilled"
            ? lastRes.value.data.totalPrice || 0
            : 0,
        );
        setCurrentTotalExpense(
          currentTotalRes.status === "fulfilled"
            ? currentTotalRes.value.data || 0
            : 0,
        );
        setTotalIssued(
          totalIssuedRes.status === "fulfilled"
            ? totalIssuedRes.value.data || 0
            : 0,
        );
        setCurrentCutoffExpense(
          currentCutoffRes.status === "fulfilled"
            ? currentCutoffRes.value.data || 0
            : 0,
        );
      } catch (error) {
        console.error("Error fetching widget data:", error);
      }
    },
    [currencyId, userLoggedID],
  );

  useEffect(() => {
    // If user previously clicked Clear Filter, stay in show-all mode on remount
    if (FilterStore.EXisAllData()) {
      setSelectedCutOff({ id: "All", from: null, to: null });
      const boundaries = getMonthBoundaries();
      const defaultCutoff = initializeCutoff(boundaries);
      setDisplayCutOff({ from: defaultCutoff.from, to: defaultCutoff.to });
      return;
    }

    const savedFilter = FilterStore.get();
    const initialCutoff =
      savedFilter ??
      (() => {
        const boundaries = getMonthBoundaries();
        return initializeCutoff(boundaries);
      })();

    setSelectedCutOff({
      id: initialCutoff.id,
      from: initialCutoff.from,
      to: initialCutoff.to,
    });
    setDisplayCutOff({ from: initialCutoff.from, to: initialCutoff.to });
  }, []);

  useEffect(() => {
    // if (selectedCutOff?.from) {
    fetchWidgetData(selectedCutOff);
    // }
  }, [selectedCutOff, currencyId, userLoggedID, fetchWidgetData]);

  const fetchCutOff = useCallback(() => {
    // Child tabs call this after approve/reject/delete, so the pending
    // badge needs to be refreshed along with the widgets
    fetchAllPendingCount();
    // If user previously clicked Clear Filter, stay in show-all mode
    if (FilterStore.EXisAllData()) {
      const allCutoff = { id: "All", from: null, to: null };
      setSelectedCutOff(allCutoff);
      fetchWidgetData(allCutoff); // zeros out widgets
      return;
    }
    if (selectedCutOff?.from) {
      fetchWidgetData(selectedCutOff);
    }
  }, [selectedCutOff, fetchWidgetData, fetchAllPendingCount]);

  const handleChangeTab = (k) => {
    setActivekeyTab(k);
    FilterStore.saveTab(k);
  };

  const handleDateRangeChange = (startDate, endDate) => {
    FilterStore.EXclearAllData(); // user picked a new date → exit show-all mode
    const cutoff = { id: "custom", from: startDate, to: endDate };
    setSelectedCutOff(cutoff);
    setDisplayCutOff({ from: startDate, to: endDate });
    FilterStore.save(startDate, endDate);
  };

  const [clearFilterSignal, setClearFilterSignal] = useState(0);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [displayCutOff, setDisplayCutOff] = useState({ from: null, to: null });

  const handleClearFilter = () => {
    // Persist the "show all" flag so remount also skips date restriction
    FilterStore.EXsaveAllData();
    FilterStore.save(null, null); // wipe any saved date range

    const allCutoff = { id: "All", from: null, to: null };
    setSelectedCutOff(allCutoff); // zeros widgets via fetchWidgetData effect

    // Tell child tabs to reset their own search / filterColumn state
    setClearFilterSignal((n) => n + 1);
    const boundaries = getMonthBoundaries();
    const defaultCutoff = initializeCutoff(boundaries);
    setDisplayCutOff({ from: defaultCutoff.from, to: defaultCutoff.to });
    setDatePickerKey((n) => n + 1);
  };

  const widgetList = [
    {
      widgetOneTitle: "Last cut-off Expenses",
      widgetOneAmount: lastCutoffExpenses,
      widgetTwoTitle: "Current Total Expense",
      widgetTwoAmount: currentTotalExpense,
    },
    {
      widgetOneTitle: "Total Issued",
      widgetOneAmount: totalIssued,
      widgetTwoTitle: "Current cut-off Expense",
      widgetTwoAmount: currentCutoffExpense,
    },
  ];

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
          />
        </div>
      ) : authrztn.includes("Expenses-View") ? (
        <>
          <div className="w-100 p-2 mb-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">EXPENSES</span>
            </div>
            <div className="d-flex">
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-success dropdown-toggle pt-2 px-3 me-2"
                  data-bs-toggle="dropdown"
                >
                  Export
                </button>
                <ul className="dropdown-menu">
                  <li
                    className="dropdown-item border-bottom"
                    style={{ cursor: "pointer" }}
                    onClick={() => setExportFormat("pdf")}
                  >
                    PDF
                  </li>
                  <li
                    className="dropdown-item"
                    style={{ cursor: "pointer" }}
                    onClick={() => setExportFormat("excel")}
                  >
                    Excel
                  </li>
                </ul>
              </div>
              {authrztn.includes("Expenses-Add") && (
                <Link
                  to="/accounting/add-expenses/null/null"
                  className="btn btn-primary d-flex flex-row align-items-center title-button"
                >
                  <i className="bx bx-plus fs-5"></i> Create
                </Link>
              )}
            </div>
          </div>

          <CurrencySelector
            setWidgetCurrencySymbol={setWidgetCurrencySymbol}
            currencyId={currencyId}
            setCurrencyId={setCurrencyId}
            style={{ maxWidth: "8.5rem" }}
          />

          <div className="container-fluid position-relative">
            {/* Widgets */}
            <div className="mt-3 mb-4">
              {widgetList.map((item, index) => (
                <div className="d-flex mb-3" key={index}>
                  <div className="col-sm w-100 payable-card px-2">
                    <div
                      className="d-flex flex-row align-items-center justify-content-between border p-3 shadow-sm rounded hover-card"
                      style={{
                        cursor: "pointer",
                        transition: "transform 0.2s",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-center gap-2 text-secondary">
                        <i className="bx bx-bar-chart-alt fs-4"></i>
                        <h3 className="fs-6 mb-0">{item.widgetOneTitle}</h3>
                      </div>
                      <div className="d-flex text-nowrap payable-card-desc">
                        <h1
                          className="fs-5 fw-bold mb-0"
                          title={formatToTwoDecimal(item?.widgetOneAmount)}
                          style={{
                            cursor: "pointer",
                            color: "var(--widget-number-color)",
                          }}
                        >
                          {widgetCurrencySymbol}{" "}
                          {formatToTwoDecimal(item?.widgetOneAmount)}
                        </h1>
                      </div>
                    </div>
                  </div>

                  {/* Widget Column #2 */}
                  <div className="col-sm w-100 px-2">
                    <div
                      className="d-flex align-items-center justify-content-between w-100 p-3 border shadow-sm rounded hover-card"
                      style={{
                        cursor: "pointer",
                        transition: "transform 0.2s",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-center gap-2 text-secondary">
                        <i className="bx bx-wallet fs-4 mb-0"></i>
                        <h3 className="fs-6 mb-0">{item.widgetTwoTitle}</h3>
                      </div>
                      <div className="d-flex flex-column text-nowrap payable-card-desc">
                        <h1
                          className="fs-5 fw-bold mb-0"
                          title={formatToTwoDecimal(item?.widgetTwoAmount)}
                          style={{
                            fontSize: "2rem",
                            cursor: "pointer",
                            color: "var(--widget-number-color)",
                          }}
                        >
                          {widgetCurrencySymbol}{" "}
                          {formatToTwoDecimal(item?.widgetTwoAmount)}
                        </h1>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {activekeyTab === "approved" && (
              <div className="mt-4 p-0 d-flex align-items-end gap-2">
                <DateRangePicker
                  key={datePickerKey}
                  startDate={displayCutOff?.from}
                  endDate={displayCutOff?.to}
                  onDateRangeChange={handleDateRangeChange}
                  label="Date Range"
                />
                {/* </div>
                <div className="col-sm d-flex flex-row align-items-end mb-2 filter-btn-container w-100"></div>
              </div> */}
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClearFilter}
                  title="Remove date filter and show all records"
                >
                  <i className="fa-solid fa-filter-circle-xmark me-1"></i>
                  Clear Filter
                </button>
              </div>
            )}
            <div className="container-fluid mt-3">
              <Tabs
                transition={false}
                id="status-tabs"
                className="mb-3"
                activeKey={activekeyTab}
                onSelect={handleChangeTab}
              >
                <Tab
                  eventKey="pending"
                  title={
                    <span>
                      Pending{" "}
                      {pendingCount > 0 && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#000",
                            color: "#fff",
                            borderRadius: "999px",
                            fontSize: "0.7rem",
                            fontWeight: "bold",
                            minWidth: "1.4em",
                            height: "1.4em",
                            padding: "0 0.4em",
                            lineHeight: 1,
                            verticalAlign: "middle",
                            marginLeft: "4px",
                          }}
                        >
                          {pendingCount}
                        </span>
                      )}
                    </span>
                  }
                >
                  {activekeyTab === "pending" && (
                    <PendingStatusTab
                      authrztn={authrztn}
                      currencyId={currencyId}
                      userLoggedID={userLoggedID}
                      selectedCutOff={selectedCutOff}
                      // setSelectedCutOff={setSelectedCutOff}
                      fetchCutOff={fetchCutOff}
                      status_tab={"For-Approval"}
                      clearFilterSignal={clearFilterSignal}
                    />
                  )}
                </Tab>
                <Tab eventKey="approved" title="Approved">
                  {activekeyTab === "approved" && (
                    <PendingStatusTab
                      authrztn={authrztn}
                      currencyId={currencyId}
                      userLoggedID={userLoggedID}
                      selectedCutOff={selectedCutOff}
                      // setSelectedCutOff={setSelectedCutOff}
                      fetchCutOff={fetchCutOff}
                      status_tab={"Approved"}
                      clearFilterSignal={clearFilterSignal}
                    />
                  )}
                </Tab>
              </Tabs>
            </div>
          </div>

          <ExportOptionsModal
            exportFormat={exportFormat}
            onHide={() => setExportFormat(null)}
            config={expensesExportConfig}
            filters={{ currencyId, userLoggedID }}
            defaultRange={displayCutOff}
            onPdfReady={pdfPreview.open}
          />
          <PdfPreviewModal {...pdfPreview.modalProps} />
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}
    </div>
  );
};

export default Expenses;
